import { useState } from 'react'
import Alert from '@mui/material/Alert'
import Button from '@mui/material/Button'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { request } from '../../api/client'
import { DataState } from '../../components/DataState'
import { QueryError } from '../../components/QueryError'
import { formatDateTime } from '../../format'

interface NetworkEntry {
  id: string
  cidr: string
  label: string
  active: boolean
  created_at: string | null
  updated_at: string | null
  changed_by: string | null
}

export function MachineNetworksPanel({ endpoint, canEdit }: { endpoint: string; canEdit: boolean }) {
  const cache = useQueryClient()
  const [cidr, setCidr] = useState('')
  const [label, setLabel] = useState('')
  const [removing, setRemoving] = useState<NetworkEntry | null>(null)
  const key = ['machine-networks', endpoint]
  const list = useQuery({
    queryKey: key,
    queryFn: ({ signal }) =>
      request<NetworkEntry[]>(endpoint, { signal }),
  })
  const add = useMutation({
    mutationFn: () =>
      request<NetworkEntry[]>(endpoint, {
      method: 'POST', body: { cidr: cidr.trim(), label: label.trim() },
    }),
    onSuccess: (entries) => {
      cache.setQueryData(key, entries)
      setCidr('')
      setLabel('')
    },
  })
  const remove = useMutation({
    mutationFn: (entry: NetworkEntry) =>
      request<void>(`${endpoint}${entry.id}/`, {
        method: 'DELETE',
      }),
    onSuccess: () => {
      setRemoving(null)
      void cache.invalidateQueries({ queryKey: key })
    },
  })

  return (
    <Paper variant="outlined" sx={{ p: 3, mt: 3 }}>
      <Typography variant="h2" gutterBottom>نشانی‌های مجاز اتصال ماشینی</Typography>
      <Alert severity="warning" sx={{ mb: 2 }}>
        فراخوانی API از نشانی خارج از این فهرست رد می‌شود. فهرست خالی اجازهٔ اتصال نمی‌دهد.
      </Alert>
      <DataState isPending={list.isPending} isError={list.isError} error={list.error}
        onRetry={() => void list.refetch()}>
        <Stack spacing={2}>
          {list.data?.length === 0 ? <Typography>هنوز نشانی مجازی ثبت نشده است.</Typography> : null}
          {list.data?.map((entry) => (
            <Stack key={entry.id} spacing={0.5}>
              <Typography><code dir="ltr">{entry.cidr}</code> — {entry.label || 'بدون برچسب'}</Typography>
              <Typography variant="caption">{entry.active ? 'فعال' : 'غیرفعال'}؛ ثبت: {formatDateTime(entry.created_at)}؛ آخرین تغییر: {formatDateTime(entry.updated_at)}</Typography>
              {entry.changed_by ? <Typography variant="caption">شناسهٔ تغییر‌دهنده: <code dir="ltr">{entry.changed_by}</code></Typography> : null}
              {canEdit ? <Button color="error" onClick={() => { remove.reset(); setRemoving(entry) }}>حذف نشانی {entry.cidr}</Button> : null}
            </Stack>
          ))}
        </Stack>
      </DataState>
      {canEdit ? (
        <Stack component="form" spacing={2} sx={{ mt: 3 }} onSubmit={(event) => { event.preventDefault(); add.mutate() }}>
          <TextField label="نشانی یا محدودهٔ شبکه" value={cidr} onChange={(event) => setCidr(event.target.value)}
            helperText="نشانی IPv4 یا IPv6 یا محدودهٔ CIDR؛ مانند 192.0.2.10 یا 2001:db8::/64. محدودهٔ همگانی پذیرفته نمی‌شود."
            required
            slotProps={{
              htmlInput: { dir: 'ltr', maxLength: 64 },
            }} />
          <TextField label="برچسب (اختیاری)" value={label} onChange={(event) => setLabel(event.target.value)}
            slotProps={{ htmlInput: { maxLength: 120 } }} />
          {add.isError ? <QueryError error={add.error} /> : null}
          <Button type="submit" variant="contained" disabled={add.isPending || !cidr.trim()}>افزودن نشانی</Button>
        </Stack>
      ) : null}
      <Dialog open={removing !== null} onClose={() => !remove.isPending && setRemoving(null)}>
        <DialogTitle>حذف نشانی مجاز</DialogTitle>
        <DialogContent>
          <Typography>فراخوانی از <code dir="ltr">{removing?.cidr}</code> پس از حذف رد خواهد شد، مگر محدودهٔ مجاز دیگری آن را پوشش دهد.</Typography>
          {remove.isError ? <QueryError error={remove.error} /> : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRemoving(null)} disabled={remove.isPending}>انصراف</Button>
          <Button color="error" disabled={remove.isPending} onClick={() => removing && remove.mutate(removing)}>حذف کن</Button>
        </DialogActions>
      </Dialog>
    </Paper>
  )
}
